import { useQuery, useQueryClient } from '@cbgms/base/http/use-query'
import { useDispatch } from 'react-redux'
import { useEffect } from 'react'
import { IEmployee } from '@cbgms/api'
import { getUserEmployee } from 'app/auth/state/actions'
import { GET_USER_EMPLOYEE_SUCCESS } from 'app/auth/state/types'

// Use the same cache key as the existing implementation for perfect compatibility
export const CURRENT_EMPLOYEE_QUERY_KEY = 'currentEmployee'

// Module-level promise cache to prevent duplicate API calls even across component remounts
let currentEmployeePromise: Promise<IEmployee> | null = null
let currentEmployeeData: IEmployee | null = null

/**
 * React Query hook to fetch current employee data with automatic deduplication.
 */
export const useCurrentEmployee = () => {
  const dispatch = useDispatch()

  const query = useQuery(
    [CURRENT_EMPLOYEE_QUERY_KEY],
    async (): Promise<IEmployee> => {
      // If we already have data, return it immediately
      if (currentEmployeeData) {
        return currentEmployeeData
      }

      // If there's already a pending promise, wait for it
      if (currentEmployeePromise) {
        return currentEmployeePromise
      }

      // Create new promise and cache it
      currentEmployeePromise = (async () => {
        const result = await dispatch(getUserEmployee())
        currentEmployeeData = result.Data
        return result.Data
      })()

      return currentEmployeePromise
    },
    {
      staleTime: 10 * 60 * 1000, // 10 minutes
      cacheTime: 15 * 60 * 1000, // 15 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false
    }
  )

  // Keep Redux (auth.employee / auth.currentShop) seeded from the employee data on every mount.
  // The queryFn short-circuits on cache hits and therefore does not dispatch getUserEmployee(),
  // so without this the currentShop reducer never seeds auth.currentShop. That leaves the
  // main-content gate (auth.shop.ID === auth.currentShop) permanently false, rendering the
  // sidebar but a blank main area. Dispatching the success action here is idempotent and does
  // not trigger an extra network request. See GMSWB-817.
  const { data } = query
  useEffect(() => {
    if (data) {
      dispatch({ type: GET_USER_EMPLOYEE_SUCCESS, payload: { data: { Data: data } } })
    }
  }, [data, dispatch])

  return query
}

/**
 * Hook to refresh current employee data
 */
export const useRefreshCurrentEmployee = () => {
  const queryClient = useQueryClient()

  return () => {
    // Clear module cache
    currentEmployeePromise = null
    currentEmployeeData = null
    queryClient.invalidateQueries([CURRENT_EMPLOYEE_QUERY_KEY])
  }
}

/**
 * Hook to prefetch current employee data
 */
export const usePrefetchCurrentEmployee = () => {
  const queryClient = useQueryClient()
  const dispatch = useDispatch()

  return () => {
    queryClient.prefetchQuery(
      [CURRENT_EMPLOYEE_QUERY_KEY],
      async (): Promise<IEmployee> => {
        const result = await dispatch(getUserEmployee())
        return result.Data
      },
      {
        staleTime: 5 * 60 * 1000
      }
    )
  }
}
